import React, { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { toast } from "../../components/ui/sonner";

const ROLE_LABEL = { manager: "Manager", estimator: "Estimator" };

// Per-user extra pages on top of their role's pages (saved instantly on tick).
export function UserPageExtras({ pages, roleCfg }) {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  useEffect(() => { api.get("/page-access/users").then((r) => setUsers(r.data || [])); }, []);

  const toggle = async (u, page, on) => {
    const next = on ? [...new Set([...(u.extra_pages || []), page])] : (u.extra_pages || []).filter((p) => p !== page);
    try {
      const { data } = await api.put(`/page-access/users/${u.id}`, { extra_pages: next });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, extra_pages: data.extra_pages } : x)));
      toast.success(`${u.name || u.email}: ${on ? "access added" : "access removed"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const shown = users.filter((u) => `${u.name} ${u.email} ${u.branch}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-3" data-testid="user-page-extras">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-800">Per-user extra pages</div>
          <p className="text-xs text-slate-500 mt-1">Give one person a page their role doesn't have. Grey ticks come from their role; changes save straight away.</p>
        </div>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search users…" data-testid="user-extras-search"
          className="h-9 border border-slate-300 rounded px-3 text-sm w-60" />
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto max-h-[480px]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">User</th>
              {pages.map(([k, label]) => <th key={k} className="px-3 py-2.5 text-center text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-extras-row-${u.id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-semibold text-slate-900">{u.name || u.email}</div>
                  <div className="text-xs text-slate-500">{ROLE_LABEL[u.role]}{u.branch ? ` · ${u.branch}` : ""}</div>
                </td>
                {pages.map(([k]) => {
                  const fromRole = (roleCfg[u.role] || []).includes(k);
                  if (k === "users" && u.role === "estimator") return <td key={k} className="px-3 py-2.5 text-center text-slate-300">—</td>;
                  return (
                    <td key={k} className="px-3 py-2.5 text-center">
                      <input type="checkbox" className="h-4 w-4 accent-slate-900 cursor-pointer disabled:cursor-default disabled:opacity-40"
                        checked={fromRole || (u.extra_pages || []).includes(k)} disabled={fromRole}
                        onChange={(e) => toggle(u, k, e.target.checked)} data-testid={`user-extra-${u.id}-${k}`} />
                    </td>
                  );
                })}
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={pages.length + 1} className="px-4 py-6 text-center text-slate-400">No users found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
